import { Button, RhLink } from "@/components";
import { ensurePostsAreInductees } from "@/core/utils";
import { usePageWrapperData } from "@/providers/PageWrapperContext";

import styles from "../RHTV-Modal.module.scss";
import className from "classnames/bind";
const cx = className.bind(styles);

// Icon images
import IconClose from '@/assets/svg/close.svg';
import IconSkip from "@/assets/svg/skip.svg";
import IconPause from "@/assets/svg/pause.svg";
import IconPlay from "@/assets/svg/arrow-right.svg";

// -------------------- Artist component

type Artist = RockHallTvVideo['rhtvVideoContent']['artists'][number];

export const Artist = ({ artist, isLast, isTrayOpen }: { artist: Artist; isLast: boolean; isTrayOpen?: boolean }) => {
  const [artistType] = artist.type;
  const isInductee = artistType === 'inductee';
  const inductee = artist.inductee?.node ? ensurePostsAreInductees({ nodes: [artist.inductee.node] })[0] : null;
  const name = isInductee ? inductee?.title : artist.name;
  const link = isInductee ? inductee?.uri : artist.nameLink?.node?.uri;
  const Tag = link ? RhLink : 'span';

  // Handle click to prevent modal close and allow navigation
  const handleClick = (e: React.MouseEvent) => {
    if (link) {
      // Stop propagation to prevent modal click handlers from interfering
      e.stopPropagation();
    }
  };

  return (
    <>
      <Tag
        className={cx("artist", { "artist-link": !!link })}
        href={link || undefined}
        data-artist-link={link ? "true" : undefined}
        {...(link && {
          tabIndex: isTrayOpen ? -1 : 0,
          onClick: handleClick
        })}
      >
        {name}
      </Tag>
      {!isLast && ', '}
    </>
  );
};

// -------------------- PlayerUI component

interface PlayerUIProps {
  channelTitle?: string;
  videoTitle?: string;
  artists?: RockHallTvVideo['rhtvVideoContent']['artists'];
  currentVideoIndex: number;
  totalVideos: number;
  isPlaying: boolean | null; // null = unknown (waiting for YouTube), true = playing, false = paused
  trayHeight: number;
  isTrayOpen: boolean;
  externalYoutubeLink?: string;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onPauseToggle: () => void;
  isPortraitMode?: boolean; // Portrait mode flag
  isTouchDevice?: boolean; // Touch device flag
}

export default function PlayerUI({
  channelTitle,
  videoTitle,
  artists,
  currentVideoIndex,
  totalVideos,
  externalYoutubeLink,
  isPlaying,
  trayHeight,
  isTrayOpen,
  onClose,
  onPrevious,
  onNext,
  onPauseToggle,
  isPortraitMode = false,
  isTouchDevice = false,
}: PlayerUIProps) {
  const { labels: { ariaLabels, rhtvWatchMoreCta } = {} } = usePageWrapperData();

  // Calculate player UI bottom row transform (moves up when tray opens)
  const getPlayerUIBottomTransform = (): string => {
    // Both portrait and landscape modes: move UI up when tray opens
    if (isTrayOpen && trayHeight > 0) {
      return `translateY(-${trayHeight}px)`;
    }
    return 'translateY(0)';
  };

  return (
    <>
      {/* Linear gradient overlay at bottom of video for text readability */}
      {!isPortraitMode && <div className={cx("gradient-overlay")} />}

      {/* Player UI elements */}
      <div className={cx("player-ui", { "portrait-mode": isPortraitMode, "touch-device": isTouchDevice })}>

        {/* Row - Top */}
        <div className={cx("player-ui-row", "top")}>
          {/* Close button */}
          <button
            className={cx("close-button")}
            aria-label={ariaLabels?.closeButton}
            onClick={onClose}
            tabIndex={isTrayOpen ? -1 : 0}
          >
            <IconClose aria-hidden="true" />
          </button>
        </div>

        {/* Row - Bottom */}
        <div className={cx("player-ui-row", "bottom")}
          style={{ transform: getPlayerUIBottomTransform() }}
        >

          {/* Video text info */}
          <div className={cx("copy")}>

            {/* Channel */}
            <p className={cx(["channel-title"])} aria-label={`Channel: ${channelTitle}`}>{channelTitle}</p>

            {/* Title */}
            <h1 className={cx(["video-title"])}>{videoTitle}</h1>

            {/* Artists */}
            {artists && (
              <p className={cx(["video-artist"])}>
                {artists.map((artist, index) => (
                  <Artist key={index} artist={artist} isLast={index === artists.length - 1} isTrayOpen={isTrayOpen} />
                ))}
              </p>
            )}
            {externalYoutubeLink && (
              <Button
                className={cx("watch-more-button")}
                noIcon
                target="_blank"
                size="small"
                url={externalYoutubeLink}
                label={rhtvWatchMoreCta}
                />
            )}
          </div>

          {/* Controls */}
          <div className={cx(["controls"])}>

            {/* Previous */}
            <button
              className={cx([
                "control",
                "prev",
                { disabled: currentVideoIndex === 0 },
              ])}
              onClick={onPrevious}
              aria-label={ariaLabels?.prevButton}
              disabled={currentVideoIndex === 0}
              tabIndex={isTrayOpen ? -1 : 0}
            >
              <IconSkip aria-hidden="true" />
            </button>

            {/* Pause/Play */}
            <button
              className={cx(["control", "pause"]) }
              onClick={onPauseToggle}
              aria-label={isPlaying === true ? ariaLabels?.pauseButton : ariaLabels?.playButton}
              aria-pressed={isPlaying === true}
              tabIndex={isTrayOpen ? -1 : 0}
            >
              {/* Show pause icon ONLY when we KNOW video is playing (isPlaying === true) */}
              {/* Show play icon when paused (false) OR unknown (null) */}
              {isPlaying === true ? <IconPause aria-hidden="true" /> : <IconPlay aria-hidden="true" />}
            </button>

            {/* Next */}
            <button
              className={cx([
                "control",
                "next",
              ])}
              onClick={onNext}
              aria-label={ariaLabels?.nextButton}
              tabIndex={isTrayOpen ? -1 : 0}
            >
              <IconSkip aria-hidden="true" />
            </button>
          </div>

        </div>
      </div>
    </>
  );
}
